import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import type * as React from "react";

import { Box } from "../../components/ui/box";
import { Icon } from "../../components/ui/icon";
import { Text } from "../../components/ui/text";
import {
  WASH_HOVER,
  type SurfaceToken,
} from "../../components/ui/box-classes";
import { cn } from "../../lib/utils";

import {
  useDataTableDensity,
  useDataTableHeaderDrag,
  useDataTableSurface,
} from "./data-table-context";
import type {
  DataTableColumnHeaderProps,
  DataTableColumnMeta,
} from "./data-table-types";
import { DENSITY_CONFIG } from "./data-table-utils";

/* Pressed and keyboard-focused header. Written out whole because the modifier
   is part of the class and Tailwind only emits what it can read verbatim. */
const HEADER_PRESSED_CLASS =
  "active:bg-wash-active focus-visible:bg-wash-selected";

const HEADER_HOVER_GRADIENT_STYLE: React.CSSProperties = {
  backgroundImage:
    "linear-gradient(to right, transparent 0%, var(--muted) 100%)",
};

function renderTitle(title: React.ReactNode) {
  // size={null} — the density header text class on TableHead inherits down.
  // Headers are drag/sort surfaces; the header row's select-none inherits too.
  return typeof title === "string" || typeof title === "number" ? (
    <Text size={null} weight="medium" truncate>
      {title}
    </Text>
  ) : (
    title
  );
}

/**
 * Purely decorative: the gradient wash that replaces the flat hover fill on
 * columns that opt in. It carries no information and takes no input
 * (`pointerEvents="none"`), so it stays out of the accessibility tree — the
 * header control itself is what a user, a screen reader and a test address.
 * It publishes `data-slot` because it is the only trace of the opt-in.
 */
function HoverGradientOverlay() {
  return (
    <Box hoverReveal="colhead" pointerEvents="none" fill="absolute" zIndex={0}
      aria-hidden="true"
      data-slot="column-header-hover"
      className="group-active/colhead:opacity-100"
      style={HEADER_HOVER_GRADIENT_STYLE}
    />
  );
}

export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
}: DataTableColumnHeaderProps<TData, TValue>): React.ReactElement {
  const canSort = column.getCanSort();
  const meta = column.columnDef.meta as DataTableColumnMeta | undefined;
  const density = useDataTableDensity();
  const surface = useDataTableSurface();
  const headerDrag = useDataTableHeaderDrag();
  const canReorder = headerDrag?.reorderableIds.has(column.id) ?? false;
  const densityConfig = DENSITY_CONFIG[density];
  const contentAlignmentClass = meta?.isNumeric
    ? "justify-end"
    : "justify-start";
  const usesGradientHover = meta?.headerHoverStyle === "gradient";
  const contentTestId = `data-table-column-header-content-${column.id}`;

  if (!canSort) {
    return (
      <Box group="colhead" fill="height" minWidth={0} flex={1} overflow="hidden"
        direction="row"
        align="center"
        className={cn(
          "w-full",
          "transition-[background-color]",
          contentAlignmentClass,
          densityConfig.headerPadding,
          canReorder && "cursor-grab",
          !usesGradientHover && WASH_HOVER,
        )}
      >
        {usesGradientHover ? <HoverGradientOverlay /> : null}
        <Box position="relative" zIndex={10} minWidth={0} flex={1}
          direction="row"
          align="center"
          data-testid={contentTestId}
          className={cn(contentAlignmentClass)}
        >
          {renderTitle(title)}
        </Box>
      </Box>
    );
  }

  const sorted = column.getIsSorted();
  const SortIcon =
    sorted === "asc" ? ArrowUp : sorted === "desc" ? ArrowDown : ArrowUpDown;

  return (
    <Box fill="height" minWidth={0} flex={1} alignSelf="stretch" overflow="hidden"
      as="button"
      direction="row"
      align="center"
      onClick={() => {
        // A column-reorder drag ends with a pointer-up on this button;
        // swallow the resulting click so dropping a column never sorts it.
        if (headerDrag?.didDragRef.current) return;
        const current = column.getIsSorted();
        if (!current) {
          column.toggleSorting(false);
        } else if (current === "asc") {
          column.toggleSorting(true);
        } else {
          column.clearSorting();
        }
      }}
      className={cn(
        "w-full",
        contentAlignmentClass,
        densityConfig.headerPadding,
        cn(
          "group/colhead outline-none transition-[background-color,box-shadow] focus-visible:ring-[3px] focus-visible:ring-ring/50",
          canReorder ? "cursor-grab" : "cursor-pointer",
          usesGradientHover
            ? "active:bg-transparent focus-visible:bg-transparent"
            : cn(
                WASH_HOVER,
                HEADER_PRESSED_CLASS,
              ),
        ),
      )}
    >
      {usesGradientHover ? <HoverGradientOverlay /> : null}
      <Box position="relative" zIndex={10} minWidth={0} flex={1}
        direction="row"
        align="center"
        data-testid={contentTestId}
        className={cn(contentAlignmentClass)}
      >
        {renderTitle(title)}
        <Icon
          as={SortIcon}
          size="md"
          className={cn(
            "ml-1 shrink-0",
            sorted
              ? "text-foreground"
              : "text-muted-foreground/0 group-hover/colhead:text-muted-foreground/40",
          )}
        />
      </Box>
    </Box>
  );
}
